import { useState, useEffect, useRef, type RefObject } from 'react';
import { Settings2 } from 'lucide-react';
import { usePlayerStore, type LevelInfo } from '@/store/usePlayerStore';
import { useSettingsStore } from '@/store/useSettingsStore';
import { getActiveHls } from './useHls';
import { getActiveDashPlayer } from './useDash';

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

interface SettingsMenuProps {
  videoRef: RefObject<HTMLVideoElement | null>;
}

export function SettingsMenu({ videoRef }: SettingsMenuProps) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const levels = usePlayerStore((s) => s.levels);
  const currentLevel = usePlayerStore((s) => s.currentLevel);
  const setCurrentLevel = usePlayerStore((s) => s.setCurrentLevel);
  const playbackRate = useSettingsStore((s) => s.playbackRate);
  const setPlaybackRate = useSettingsStore((s) => s.setPlaybackRate);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleLevelChange = (value: number) => {
    setCurrentLevel(value);
    // Try HLS first, then dash.js.
    const hls = getActiveHls();
    if (hls) {
      hls.currentLevel = value;
      return;
    }
    const dash = getActiveDashPlayer();
    if (dash) {
      // -1 = auto (don't set quality, let ABR decide)
      if (value >= 0) {
        dash.setRepresentationForTypeByIndex('video', value);
      }
    }
  };

  const handleSpeedChange = (rate: number) => {
    setPlaybackRate(rate);
    const video = videoRef.current;
    if (video) video.playbackRate = rate;
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen((o) => !o);
  };

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={handleButtonClick}
        aria-label="Settings"
        title="Settings"
        className="p-1.5 rounded hover:bg-white/10 transition-colors"
      >
        <Settings2 className="w-4 h-4" />
      </button>

      {open && (
        <div
          className="absolute bottom-full right-0 mb-2 w-52 bg-black/95 backdrop-blur rounded-md shadow-xl border border-white/10 text-white text-sm py-2 max-h-80 overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Quality */}
          {levels.length > 0 && (
            <div>
              <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-white/40 font-semibold">
                Quality
              </div>
              <label className="flex items-center gap-2 px-3 py-1.5 hover:bg-white/5 cursor-pointer">
                <input
                  type="radio"
                  name="quality"
                  checked={currentLevel === -1}
                  onChange={() => handleLevelChange(-1)}
                  className="accent-accent"
                />
                <span>Auto</span>
              </label>
              {levels.map((level: LevelInfo, i: number) => (
                <label
                  key={i}
                  className="flex items-center gap-2 px-3 py-1.5 hover:bg-white/5 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="quality"
                    checked={currentLevel === i}
                    onChange={() => handleLevelChange(i)}
                    className="accent-accent"
                  />
                  <span>{level.name}</span>
                  <span className="ml-auto text-white/40 text-[10px]">
                    {Math.round(level.bitrate / 1000)} kbps
                  </span>
                </label>
              ))}
            </div>
          )}

          {/* Speed */}
          <div className={levels.length > 0 ? 'mt-1 border-t border-white/10 pt-1' : ''}>
            <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-white/40 font-semibold">
              Speed
            </div>
            {SPEEDS.map((rate) => (
              <label
                key={rate}
                className="flex items-center gap-2 px-3 py-1.5 hover:bg-white/5 cursor-pointer"
              >
                <input
                  type="radio"
                  name="speed"
                  checked={playbackRate === rate}
                  onChange={() => handleSpeedChange(rate)}
                  className="accent-accent"
                />
                <span>{rate}x</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
